import classNames from "classnames";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { toastActions } from "domain/toast/toastActions";
import { join, orderBy } from "lodash";
import React, { ComponentProps, useRef, useState } from "react";
import { FieldPath, FieldValues, FormState, useController, UseControllerProps } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Spinner } from "reactstrap";
import { Message } from "shared/components/Message";
import { getFileExtension } from "shared/fileUtils";
import { useS3Upload } from "shared/hooks/useS3Upload";
import { sharedMessages } from "shared/sharedMessages";
import { TranslatedUseControllerOptions, useTranslatedValidations } from "shared/validations/useTranslatedValidations";
import styles from "./S3Upload.module.scss";

export enum UploadStatus {
    NotStarted,
    Started,
    Failed,
    Complete,
    Invalid,
    Existing,
    Empty,
}

interface IProps<TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues>>
    extends Omit<UseControllerProps<TFieldValues, TFieldName>, "rules" | "defaultValue"> {
    allowedFileExtensions: string[];
    id?: string;
    rules?: TranslatedUseControllerOptions<TFieldValues, TFieldName>;
    onChange?: () => void;
    getInitialStatus?: (formState: FormState<TFieldValues>) => UploadStatus | null;
}

export const S3Upload = <TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues>>({
    name,
    rules,
    allowedFileExtensions,
    id,
    onChange,
    getInitialStatus,
}: IProps<TFieldValues, TFieldName>) => {
    const dispatch = useDispatch();
    const translatedRules = useTranslatedValidations<TFieldValues, TFieldName>(rules);
    const inputRef = useRef<HTMLInputElement>(null);
    const { upload } = useS3Upload();

    const { field, fieldState: { isTouched, invalid, error }, formState } = useController({
        name,
        rules: translatedRules,
    });
    const [status, setStatus] = useState<UploadStatus>(getInitialStatus?.(formState) ?? UploadStatus.NotStarted);

    const normalizedAllowedExtensions = allowedFileExtensions
        .map(allowedFileExt => allowedFileExt.replace(/^\./, "").toLowerCase());
    const accept = normalizedAllowedExtensions
        .map(ext => `.${ext}`)
        .join(",");

    return (
        <div className={classNames("upload", styles.upload, isTouched && ["was-validated", invalid ? "invalid" : "valid"])}>
            <div className="input-group m-0" style={{ cursor: 'pointer' }} onClick={() => inputRef.current?.click()}>
                <div className="input-group-prepend">
                    <span className="input-group-text h-100">
                        <Message message={sharedMessages.browseLabel} />
                    </span>
                </div>
                <div className="form-control message d-flex align-items-center" aria-live="polite">
                    <Message {...getMessageProps(status)} />
                    {status === UploadStatus.Started && (
                        <span data-testid="uploadSpinner">
                            <Spinner className="ml-2" />
                        </span>
                    )}
                </div>
            </div>

            <input
                id={id || name}
                name={name}
                type="file"
                className={classNames("form-control", styles.hidden)}
                accept={accept}
                ref={(e) => {
                    field.ref(e);
                    (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
                }}
                data-testid="uploadInput"
                onChange={onFileInputChange}
            />

            {error && (
                <div className="invalid-feedback d-block">
                    {error.message}
                </div>
            )}

            {status === UploadStatus.Invalid && (
                <ErrorMessage className="mt-1" message={sharedMessages.uploadInvalidMessage} messageValues={{ allowed: join(orderBy(allowedFileExtensions), ", ") }} />
            )}

            {status === UploadStatus.Empty && (
                <ErrorMessage className="mt-1" message={sharedMessages.uploadEmptyMessage} />
            )}
        </div>
    );

    async function onFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (files?.length !== 1) {
            if (inputRef.current) {
                inputRef.current.value = "";
            }
            return;
        }

        const file = files[0];
        const fileExt = getFileExtension(file);
        if (!fileExt || !normalizedAllowedExtensions.includes(fileExt)) {
            onFieldChange(null);
            if (inputRef.current) {
                inputRef.current.value = "";
            }

            setStatus(UploadStatus.Invalid);
            return;
        }

        if (file.size === 0) {
            onFieldChange(null);
            if (inputRef.current) {
                inputRef.current.value = "";
            }

            setStatus(UploadStatus.Empty);
            return;
        }

        setStatus(UploadStatus.Started);
        try {
            const objectKey = await upload(file);
            onFieldChange(objectKey);
            setStatus(UploadStatus.Complete);
        } catch {
            onFieldChange(null);
            if (inputRef.current) {
                inputRef.current.value = "";
            }

            setStatus(UploadStatus.Failed);
            dispatch(toastActions.customFailureToast(sharedMessages.uploadFailedMessage));
        }
    }

    function onFieldChange(value: string | null) {
        field.onChange(value);
        onChange?.();
        field.onBlur();
    }
};

function getMessageProps(status: UploadStatus): ComponentProps<typeof Message> {
    switch (status) {
        case UploadStatus.Existing: return { message: sharedMessages.uploadExistingMessage };
        case UploadStatus.Started: return { message: sharedMessages.uploadProgressMessage };
        case UploadStatus.Failed: return { message: sharedMessages.uploadFailedMessage };
        case UploadStatus.Complete: return { message: sharedMessages.uploadCompleteMessage };
        default: return { message: sharedMessages.uploadChooseFilePlaceholder };
    }
}
